import { useEffect, useRef } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/lib/local/db";
import { useFiscalStore } from "@/store/fiscal-store";
import { syncLocalDBToStore, hydrateNotasWithItems } from "@/lib/local/sync";
import { useLocation } from "react-router-dom";

/**
 * Componente de monitoramento reativo.
 * Garante que o estado global (Zustand) reflita fielmente o banco local (IndexedDB).
 */
export function LocalSyncWatcher() {
  const userId = useFiscalStore(s => s.userId);
  const location = useLocation();
  const isSyncing = useRef(false);
  const lastProcessedCount = useRef<number>(-1);
  const lastProcessedCache = useRef<number>(-1);
  
  // Observa o contador de notas e o cache para este usuário
  const count = useLiveQuery(
    () => userId ? db.notas.where("userId").equals(userId).count() : 0,
    [userId]
  );

  const lastUpdate = useLiveQuery(
    async () => {
      if (!userId) return null;
      const rows = await db.analyticsCache.where("userId").equals(userId).sortBy("updatedAt");
      return rows.length ? rows[rows.length - 1] : null;
    },
    [userId]
  );

  useEffect(() => {
    const cacheTime = lastUpdate?.updatedAt ?? 0;
    const hasChanged = count !== lastProcessedCount.current || cacheTime !== lastProcessedCache.current;

    const storeNotas = useFiscalStore.getState().notas;
    const isHydrated = storeNotas.length > 0 && (storeNotas[0].itens?.length ?? 0) > 0;
    const needsDeepAnalysis = [
      "/dashboard", "/insights", "/analytics", "/produtos", 
      "/classificacao", "/tributacao", "/notas", "/inspecao", "/relatorio",
      "/fornecedores", "/clientes", "/financeiro"
    ].some(path => location.pathname.startsWith(path));

    const needsHydrationNow = needsDeepAnalysis && !isHydrated && storeNotas.length > 0;

    // Só sincroniza se houver mudança REAL no banco ou se precisar hidratar itens para a rota atual
    if (!userId || isSyncing.current || (!hasChanged && !needsHydrationNow)) return;

    const runSync = async () => {
      isSyncing.current = true;
      try {
        if (hasChanged) {
          console.log(`[watcher] Sincronizando dados: count=${count}, cache=${cacheTime}`);
          // 1. Sincroniza metadados e KPIs básicos
          await syncLocalDBToStore();
          lastProcessedCount.current = count ?? 0;
          lastProcessedCache.current = cacheTime;
        }

        // 2. Hidrata itens se estivermos em páginas que exigem análise profunda
        if (needsDeepAnalysis) {
          await hydrateNotasWithItems();
        }

      } catch (err) {
        console.error("[watcher] Erro na sincronização reativa:", err);
      } finally {
        isSyncing.current = false;
      }
    };

    runSync();
  }, [count, lastUpdate?.updatedAt, userId, location.pathname]);

  return null;
}
